import httpService from '@/utils/httpService';

const api = 'api';

// The cleanup preview route is absent from the ratified public contract (WS-14 section 8.2).
// The client keeps the code path but never issues the request, so an accidental caller gets a
// typed capability problem instead of a 404 from a route the server does not expose.
export const RELATIONSHIP_CLEANUP_PREVIEW_AVAILABLE = false;

export const RELATIONSHIP_PREVIEW_MODES = Object.freeze({
  RESOLVE: 'resolve',
  SYNC_FIELDS: 'syncFields',
});

// Canonical server vocabulary for RegisterRelationships.MatchMode. The value is persisted and
// hashed into the resolution fingerprint, so it must match the API literal exactly.
export const RELATIONSHIP_DEFAULT_MATCH_MODE = 'trimCaseInsensitive';

export const RELATIONSHIP_APPROVAL_PURPOSES = Object.freeze([
  'confirmRun',
  'confirmCleanup',
  'relationshipConfig',
  'relationshipDelete',
  'detachLink',
  'reattachLink',
  'dismissDetach',
  'detachAnchor',
  'reapplyAnchor',
  'dynamicSchemaChange',
]);

// Mirrors RelationshipPatchRequestDto exactly. sourceFieldKey is deliberately absent: the source
// field is immutable after declaration, and the server rejects unknown members with 400.
export const RELATIONSHIP_PATCH_FIELDS = Object.freeze([
  'name',
  'targetKind',
  'targetContext',
  'matchField',
  'matchMode',
  'onMiss',
  'applicability',
  'mapping',
  'createDefaults',
  'syncMode',
  'displayRole',
  'runOnImport',
]);

const RELATIONSHIP_DECLARATION_FIELDS = Object.freeze([
  'name',
  'sourceFieldKey',
  'targetKind',
  'targetContext',
  'matchField',
  'matchMode',
  'onMiss',
  'applicability',
  'mapping',
  'createDefaults',
  'syncMode',
  'displayRole',
  'runOnImport',
]);

function requestProblem({
  status,
  code,
  detail,
  errors,
}) {
  return {
    status,
    code,
    title: 'The relationship request is invalid',
    detail,
    traceId: null,
    errors,
  };
}

function requestValidationProblem(path, detail, fieldCode = 'VALIDATION_FAILED') {
  return requestProblem({
    status: 422,
    code: 'VALIDATION_FAILED',
    detail,
    errors: [{ path, code: fieldCode, message: detail }],
  });
}

function unknownFieldProblem(path, detail) {
  return requestProblem({
    status: 400,
    code: 'UNKNOWN_FIELD',
    detail,
    errors: [{ path, code: 'UNKNOWN_FIELD', message: detail }],
  });
}

/**
 * Preserve PATCH tri-state semantics: undefined means omitted, targetContext:null
 * is the sole clear operation, and null cannot silently clear any other field.
 */
export function buildRelationshipPatchRequest(patch) {
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) {
    throw requestValidationProblem('', 'A relationship patch object is required.');
  }

  const request = {};
  const nullErrors = [];
  for (const field of Object.keys(patch)) {
    if (!RELATIONSHIP_PATCH_FIELDS.includes(field)) {
      throw unknownFieldProblem(`/${field}`, `Unknown relationship patch field: ${field}.`);
    }

    const value = patch[field];
    if (value === undefined) continue;
    if (value === null && field !== 'targetContext') {
      nullErrors.push({
        path: `/${field}`,
        code: 'VALIDATION_FAILED',
        message: 'This member cannot be cleared.',
      });
      continue;
    }
    request[field] = value;
  }

  if (nullErrors.length) {
    throw requestProblem({
      status: 422,
      code: 'VALIDATION_FAILED',
      detail: nullErrors[0].message,
      errors: nullErrors,
    });
  }
  if (!Object.keys(request).length) {
    throw requestValidationProblem('/', 'At least one patch member is required.');
  }

  return request;
}

/**
 * Keep the create body aligned with RelationshipDeclarationRequestDto. Server-owned
 * scope, enablement, ambiguity and reconcile fields are deliberately absent.
 */
export function buildRelationshipDeclarationRequest(declaration) {
  if (!declaration || typeof declaration !== 'object' || Array.isArray(declaration)) {
    throw requestValidationProblem('', 'A relationship declaration object is required.');
  }

  for (const field of Object.keys(declaration)) {
    if (!RELATIONSHIP_DECLARATION_FIELDS.includes(field)) {
      throw unknownFieldProblem(`/${field}`, `Unknown relationship declaration field: ${field}.`);
    }
  }

  const missing = RELATIONSHIP_DECLARATION_FIELDS.filter(
    (field) => !Object.prototype.hasOwnProperty.call(declaration, field),
  );
  if (missing.length) {
    throw requestValidationProblem(`/${missing[0]}`, `${missing[0]} is required.`, 'required');
  }
  if (!Array.isArray(declaration.mapping)) {
    throw requestValidationProblem('/mapping', 'mapping must be an array.');
  }
  if (!Array.isArray(declaration.createDefaults)) {
    throw requestValidationProblem('/createDefaults', 'createDefaults must be an array.');
  }

  return RELATIONSHIP_DECLARATION_FIELDS.reduce((request, field) => {
    request[field] = declaration[field];
    return request;
  }, {});
}

function assertExactFields(value, allowedFields, pointer, subject) {
  for (const field of Object.keys(value)) {
    if (!allowedFields.includes(field)) {
      throw unknownFieldProblem(`${pointer}/${field}`, `Unknown ${subject} field: ${field}.`);
    }
  }
}

function buildRelationshipPreviewScope(scope, allowedKinds) {
  if (!scope || typeof scope !== 'object' || Array.isArray(scope)) {
    throw requestValidationProblem('/scope', 'A preview scope is required.', 'required');
  }
  assertExactFields(scope, ['kind', 'rowIds', 'dataQuery'], '/scope', 'preview scope');
  if (!allowedKinds.includes(scope.kind)) {
    throw requestValidationProblem('/scope/kind', 'This preview scope is not available.');
  }

  if (scope.kind === 'rows') {
    if (!Array.isArray(scope.rowIds) || !scope.rowIds.length) {
      throw requestValidationProblem('/scope/rowIds', 'Choose at least one row.', 'required');
    }
    if (Object.prototype.hasOwnProperty.call(scope, 'dataQuery')) {
      throw unknownFieldProblem('/scope/dataQuery', 'Data filters are valid only for filtered rows.');
    }
    const rowIds = scope.rowIds.map((rowId, index) => {
      if (typeof rowId !== 'string' || !rowId.trim()) {
        throw requestValidationProblem(`/scope/rowIds/${index}`, 'A selected row is invalid.');
      }
      return rowId.trim();
    });
    return { kind: 'rows', rowIds };
  }

  if (scope.kind === 'filtered') {
    if (Object.prototype.hasOwnProperty.call(scope, 'rowIds')) {
      throw unknownFieldProblem('/scope/rowIds', 'Selected row IDs are valid only for selected rows.');
    }
    const dataQuery = scope.dataQuery;
    if (!dataQuery || typeof dataQuery !== 'object' || Array.isArray(dataQuery)) {
      throw requestValidationProblem('/scope/dataQuery', 'The current Data filters are required.', 'required');
    }
    assertExactFields(
      dataQuery,
      ['search', 'columnFilters'],
      '/scope/dataQuery',
      'Data filter',
    );
    const requestQuery = {};
    if (Object.prototype.hasOwnProperty.call(dataQuery, 'search')) {
      if (typeof dataQuery.search !== 'string' || !dataQuery.search.trim()) {
        throw requestValidationProblem('/scope/dataQuery/search', 'Search must not be blank.');
      }
      requestQuery.search = dataQuery.search.trim();
    }
    if (Object.prototype.hasOwnProperty.call(dataQuery, 'columnFilters')) {
      if (!dataQuery.columnFilters || typeof dataQuery.columnFilters !== 'object'
        || Array.isArray(dataQuery.columnFilters)) {
        throw requestValidationProblem(
          '/scope/dataQuery/columnFilters',
          'Column filters must be an object.',
        );
      }
      const columnFilters = {};
      Object.entries(dataQuery.columnFilters).forEach(([column, rawValue]) => {
        if (!column.trim()) {
          throw requestValidationProblem('/scope/dataQuery/columnFilters', 'A filter column is invalid.');
        }
        if (typeof rawValue !== 'string' || !rawValue.trim()) {
          throw requestValidationProblem(
            `/scope/dataQuery/columnFilters/${column}`,
            'A column filter value must not be blank.',
          );
        }
        columnFilters[column] = rawValue.trim();
      });
      if (Object.keys(columnFilters).length) requestQuery.columnFilters = columnFilters;
    }
    if (!requestQuery.search && !requestQuery.columnFilters) {
      throw requestValidationProblem(
        '/scope/dataQuery',
        'Filter or search in Data before choosing filtered rows.',
      );
    }
    return { kind: 'filtered', dataQuery: requestQuery };
  }

  if (Object.prototype.hasOwnProperty.call(scope, 'rowIds')) {
    throw unknownFieldProblem('/scope/rowIds', 'Selected row IDs are valid only for selected rows.');
  }
  if (Object.prototype.hasOwnProperty.call(scope, 'dataQuery')) {
    throw unknownFieldProblem('/scope/dataQuery', 'Data filters are valid only for filtered rows.');
  }
  return { kind: scope.kind };
}

export function buildRelationshipPreviewRequest(request, { cleanup = false } = {}) {
  if (!request || typeof request !== 'object' || Array.isArray(request)) {
    throw requestValidationProblem('', 'A preview request is required.');
  }
  assertExactFields(request, ['relationshipId', 'scope', 'mode'], '', 'preview request');
  if (typeof request.relationshipId !== 'string' || !request.relationshipId.trim()) {
    throw requestValidationProblem('/relationshipId', 'A relationship is required.', 'required');
  }
  const mode = request.mode === undefined ? RELATIONSHIP_PREVIEW_MODES.RESOLVE : request.mode;
  if (!Object.values(RELATIONSHIP_PREVIEW_MODES).includes(mode)) {
    throw requestValidationProblem('/mode', 'This preview mode is not available.');
  }
  if (cleanup && mode !== RELATIONSHIP_PREVIEW_MODES.RESOLVE) {
    throw requestValidationProblem('/mode', 'Field sync is not available for cleanup previews.');
  }

  const body = {
    relationshipId: request.relationshipId.trim(),
    scope: buildRelationshipPreviewScope(
      request.scope,
      cleanup ? ['cleanup'] : ['rows', 'filtered', 'register'],
    ),
  };
  // Keep ordinary resolve/create/link requests wire-compatible with the original
  // contract. The explicit mode is sent only for the manual existing-target sync.
  if (mode === RELATIONSHIP_PREVIEW_MODES.SYNC_FIELDS) body.mode = mode;
  return body;
}

export function buildDynamicDefinitionMutationRequest(request, operation, definitionRef) {
  if (!request || typeof request !== 'object' || Array.isArray(request)) {
    throw requestValidationProblem('', 'A dynamic-definition request object is required.');
  }
  if (typeof request.approvalGrantToken !== 'string' || !request.approvalGrantToken.trim()) {
    throw requestValidationProblem('/approvalGrantToken', 'approvalGrantToken is required.', 'required');
  }
  if (!request.command || typeof request.command !== 'object' || Array.isArray(request.command)) {
    throw requestValidationProblem('/command', 'command is required.', 'required');
  }
  for (const field of Object.keys(request)) {
    if (!['approvalGrantToken', 'command'].includes(field)) {
      throw unknownFieldProblem(`/${field}`, `Unknown dynamic-definition request field: ${field}.`);
    }
  }
  if (!['create', 'update', 'retire'].includes(operation) || request.command.operation !== operation) {
    throw requestValidationProblem('/command/operation', 'The command operation does not match the route.');
  }
  if (operation !== 'create'
    && (typeof definitionRef !== 'string' || !definitionRef.trim()
      || request.command.definitionRef !== definitionRef)) {
    throw requestValidationProblem(
      '/command/definitionRef',
      'command.definitionRef must match the definitionRef route value.',
      'ROUTE_BODY_IDENTITY_MISMATCH',
    );
  }

  const allowedCommandFields = operation === 'create'
    ? ['operation', 'targetKind', 'targetContext', 'storeKey', 'attributeKey', 'dataType', 'displayLabel']
    : operation === 'update'
      ? ['operation', 'definitionRef', 'displayLabel']
      : ['operation', 'definitionRef'];
  for (const field of Object.keys(request.command)) {
    if (!allowedCommandFields.includes(field)) {
      throw unknownFieldProblem(
        `/command/${field}`,
        `Unknown ${operation} command field: ${field}.`,
      );
    }
  }

  if (operation === 'update'
    && (typeof request.command.displayLabel !== 'string' || !request.command.displayLabel.trim())) {
    throw requestValidationProblem('/command/displayLabel', 'displayLabel is required.', 'required');
  }
  if (operation === 'create') {
    const requiredFields = ['targetKind', 'storeKey', 'attributeKey', 'dataType', 'displayLabel'];
    const missingField = requiredFields.find(
      (field) => typeof request.command[field] !== 'string' || !request.command[field].trim(),
    );
    if (missingField) {
      throw requestValidationProblem(`/command/${missingField}`, `${missingField} is required.`, 'required');
    }
  }

  return {
    approvalGrantToken: request.approvalGrantToken,
    command: operation === 'create'
      ? allowedCommandFields.reduce((command, field) => {
        if (request.command[field] !== undefined) command[field] = request.command[field];
        return command;
      }, {})
      : operation === 'update'
        ? {
        operation,
        definitionRef,
        displayLabel: request.command.displayLabel,
        }
        : { operation, definitionRef },
  };
}

/**
 * Generate a client request key without coupling the feature to a browser-only
 * crypto API. The server remains the source of truth for idempotency.
 */
export function createRelationshipRequestKey(prefix = 'relationship') {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return `${prefix}:${window.crypto.randomUUID()}`;
  }

  const random = Math.random().toString(36).slice(2);
  return `${prefix}:${Date.now().toString(36)}:${random}`;
}

function idempotencyHeaders(key) {
  return key ? { 'Idempotency-Key': key } : {};
}

function concurrencyHeaders(etag) {
  if (typeof etag === 'string' && etag.trim()) return { 'If-Match': etag };
  throw {
    status: 428,
    code: 'PRECONDITION_REQUIRED',
    title: 'A concurrency precondition is required',
    detail: 'Refresh the resource and retry with its authoritative ETag.',
    traceId: null,
    errors: [],
  };
}

function requiredIdempotencyHeaders(key) {
  if (typeof key === 'string' && key.trim()) return idempotencyHeaders(key);
  throw {
    status: 400,
    code: 'VALIDATION_FAILED',
    title: 'An idempotency key is required',
    detail: 'Retry the command with an Idempotency-Key.',
    traceId: null,
    errors: [{
      path: '/headers/Idempotency-Key',
      code: 'required',
      message: 'Idempotency-Key is required.',
    }],
  };
}

function commandHeaders({ requestKey = null, etag = null, requireIdempotency = true } = {}) {
  return {
    ...(requireIdempotency ? requiredIdempotencyHeaders(requestKey) : idempotencyHeaders(requestKey)),
    ...concurrencyHeaders(etag),
  };
}

function assertCleanupPreviewAvailable() {
  if (RELATIONSHIP_CLEANUP_PREVIEW_AVAILABLE) return;

  throw {
    status: 501,
    code: 'RELATIONSHIP_CAPABILITY_UNAVAILABLE',
    title: 'Cleanup previews are unavailable',
    detail: 'Removing outdated links is not part of the current relationship API.',
    traceId: null,
    errors: [],
  };
}

function assertApprovalPurpose(request) {
  if (RELATIONSHIP_APPROVAL_PURPOSES.includes(request?.purpose)) return;

  throw {
    status: 422,
    code: 'VALIDATION_FAILED',
    title: 'Approval purpose is invalid',
    detail: 'The approval request does not use a supported purpose.',
    traceId: null,
    errors: [{
      path: '/purpose',
      code: 'unknownApprovalPurpose',
      message: 'Select a supported approval purpose.',
    }],
  };
}

/**
 * Return the stable ProblemDetails fields used by both the UI and future MCP
 * clients. Keeping this normalization here prevents every dialog from making
 * different guesses about the same backend error.
 */
export function toRelationshipProblem(err, fallback = 'The relationship request failed.') {
  const response = err?.response;
  const data = response?.data || (!response && err && typeof err === 'object' ? err : null);

  if (data && typeof data === 'object') {
    const problem = {
      type: data.type || null,
      status: data.status ?? response?.status ?? null,
      code: data.code || null,
      title: data.title || null,
      detail: data.detail || fallback,
      traceId: data.traceId || null,
      errors: Array.isArray(data.errors) ? data.errors : [],
    };

    if (Object.prototype.hasOwnProperty.call(data, 'winningRunId')) {
      problem.winningRunId = data.winningRunId;
    }

    // Why an approval was refused as PREVIEW_STALE (e.g. groupScopeFingerprintChanged). Kept as
    // sent - string or { code } - and read by config/relationshipRefusalCopy.js problemReasonCode.
    if (data.reason !== undefined && data.reason !== null) {
      problem.reason = data.reason;
    }
    // The offending plan item for that refusal, when the server names one.
    if (Number.isInteger(data.planItemKey) && data.planItemKey > 0) {
      problem.planItemKey = data.planItemKey;
    }

    return problem;
  }

  return {
    type: null,
    status: response?.status || null,
    code: null,
    title: null,
    detail: typeof data === 'string' && data.trim() ? data : err?.message || fallback,
    traceId: null,
    errors: [],
  };
}

export default {
  async getCapabilities() {
    const response = await httpService.get({
      url: `${api}/relationship-capabilities`,
    });
    return response.data;
  },

  async listRelationships(registerId, { cursor = null, pageSize = null } = {}) {
    const params = {};
    if (cursor) params.cursor = cursor;
    if (pageSize) params.pageSize = pageSize;
    const response = await httpService.get({
      url: `${api}/registers/${registerId}/relationships`,
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },

  async createRelationship(registerId, declaration, requestKey) {
    const response = await httpService.post({
      url: `${api}/registers/${registerId}/relationships`,
      data: buildRelationshipDeclarationRequest(declaration),
      headers: requiredIdempotencyHeaders(requestKey),
    });
    return response.data;
  },

  async updateRelationship(registerId, relationshipId, patch, etag) {
    const response = await httpService.patch({
      url: `${api}/registers/${registerId}/relationships/${relationshipId}`,
      data: buildRelationshipPatchRequest(patch),
      headers: concurrencyHeaders(etag),
    });
    return response.data;
  },

  async validateRelationship(registerId, relationshipId) {
    const response = await httpService.post({
      url: `${api}/registers/${registerId}/relationships/${relationshipId}:validate`,
      data: {},
    });
    return response.data;
  },

  async setRelationshipEnabled(
    registerId,
    relationshipId,
    enabled,
    etag,
    approvalGrantToken = null,
    requestKey = null,
  ) {
    const command = enabled ? 'enable' : 'disable';
    const response = await httpService.post({
      url: `${api}/registers/${registerId}/relationships/${relationshipId}:${command}`,
      data: approvalGrantToken ? { approvalGrantToken } : {},
      headers: commandHeaders({ requestKey, etag, requireIdempotency: enabled }),
    });
    return response.data;
  },

  async deleteRelationship(registerId, relationshipId, approvalGrantToken, etag, requestKey) {
    const response = await httpService.delete({
      url: `${api}/registers/${registerId}/relationships/${relationshipId}`,
      data: { approvalGrantToken },
      headers: commandHeaders({ requestKey, etag }),
    });
    return response.data;
  },

  async resolveTargetSchema(registerId, targetKind, targetContext = null) {
    const response = await httpService.post({
      url: `${api}/registers/${registerId}/relationship-target-schema:resolve`,
      data: { targetKind, targetContext },
    });
    return response.data;
  },

  async createDynamicDefinition(registerId, request, requestKey) {
    const response = await httpService.post({
      url: `${api}/registers/${registerId}/relationship-target-schema/definitions`,
      data: buildDynamicDefinitionMutationRequest(request, 'create'),
      headers: requiredIdempotencyHeaders(requestKey),
    });
    return response.data;
  },

  async updateDynamicDefinition(registerId, definitionRef, request, etag, requestKey) {
    const response = await httpService.patch({
      url: `${api}/registers/${registerId}/relationship-target-schema/definitions/${definitionRef}`,
      data: buildDynamicDefinitionMutationRequest(request, 'update', definitionRef),
      headers: commandHeaders({ requestKey, etag }),
    });
    return response.data;
  },

  async retireDynamicDefinition(registerId, definitionRef, request, etag, requestKey) {
    const response = await httpService.post({
      url: `${api}/registers/${registerId}/relationship-target-schema/definitions/${definitionRef}:retire`,
      data: buildDynamicDefinitionMutationRequest(request, 'retire', definitionRef),
      headers: commandHeaders({ requestKey, etag }),
    });
    return response.data;
  },

  async previewRun(registerId, request, requestKey) {
    const response = await httpService.post({
      url: `${api}/registers/${registerId}/relationship-runs:preview`,
      data: buildRelationshipPreviewRequest(request),
      headers: requiredIdempotencyHeaders(requestKey),
    });
    return response.data;
  },

  async previewCleanup(registerId, request, requestKey) {
    assertCleanupPreviewAvailable();
    const response = await httpService.post({
      url: `${api}/registers/${registerId}/relationship-runs:preview-cleanup`,
      data: buildRelationshipPreviewRequest(request, { cleanup: true }),
      headers: requiredIdempotencyHeaders(requestKey),
    });
    return response.data;
  },

  async createApprovalGrant(request) {
    assertApprovalPurpose(request);
    const response = await httpService.post({
      url: `${api}/relationship-approval-grants`,
      data: request,
    });
    return response.data;
  },

  async confirmRun(runId, request, requestKey) {
    const response = await httpService.post({
      url: `${api}/relationship-runs/${runId}:confirm`,
      data: request,
      headers: requiredIdempotencyHeaders(requestKey),
      critical: true,
    });
    return response.data;
  },

  async cancelRun(runId) {
    const response = await httpService.post({
      url: `${api}/relationship-runs/${runId}:cancel`,
      data: {},
      critical: true,
    });
    return response.data;
  },

  async getRun(runId, { signal = null, timeout = null } = {}) {
    const requestConfig = {
      url: `${api}/relationship-runs/${runId}`,
    };
    if (signal) requestConfig.signal = signal;
    if (Number.isFinite(timeout) && timeout > 0) requestConfig.timeout = Math.ceil(timeout);
    const response = await httpService.get({
      ...requestConfig,
    });
    return response.data;
  },

  async getRunItems(runId, { cursor = null, classification = null, pageSize = null } = {}) {
    if (classification !== null && classification !== 'attention') {
      throw {
        status: 422,
        code: 'VALIDATION_FAILED',
        title: 'Run item classification is invalid',
        detail: 'The only supported run-item classification is attention.',
        traceId: null,
        errors: [{
          path: '/classification',
          code: 'unknownClassification',
          message: 'Use attention or omit the classification.',
        }],
      };
    }

    const params = {};
    if (cursor) params.cursor = cursor;
    if (classification) params.classification = classification;
    if (pageSize) params.pageSize = pageSize;

    const response = await httpService.get({
      url: `${api}/relationship-runs/${runId}/items`,
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },

  async getRunHistory(registerId, { cursor = null, pageSize = null } = {}) {
    const params = {};
    if (cursor) params.cursor = cursor;
    if (pageSize) params.pageSize = pageSize;

    const response = await httpService.get({
      url: `${api}/registers/${registerId}/relationship-runs`,
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },
};
